import { type FC } from "react";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatCountry,
  formatResolution,
  hasStallData,
} from "../model/playerFormat";
import type {
  ChartTimeUnit,
  PlayerMetrics,
  PlayerMetricsBucket,
} from "../model/types";
import { MonitoringLineChart } from "./MonitoringLineChart";

interface PlayerMetricsSectionProps {
  player: PlayerMetrics | null;
  unit: ChartTimeUnit;
  loading?: boolean;
}

interface BucketListCardProps {
  title: string;
  items: PlayerMetricsBucket[];
  loading: boolean;
  renderKey: (key: string) => string;
}

const BucketListCard: FC<BucketListCardProps> = ({
  title,
  items,
  loading,
  renderKey,
}) => {
  const { t } = useTranslation();

  let body;
  if (loading && items.length === 0) {
    body = (
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Skeleton variant="rounded" height={56} />
      </CardContent>
    );
  } else if (items.length === 0) {
    body = (
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Typography color="text.secondary">
          {t("streams.monitoring.noData")}
        </Typography>
      </CardContent>
    );
  } else {
    body = (
      <List dense sx={{ maxHeight: 280, overflow: "auto" }}>
        {items.map((item) => (
          <ListItem
            key={item.key}
            secondaryAction={
              <Typography variant="body2" color="text.secondary">
                {item.viewers}
              </Typography>
            }
          >
            <ListItemText primary={renderKey(item.key)} />
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={title}
        slotProps={{ title: { variant: "subtitle1" } }}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      {body}
    </Card>
  );
};

/**
 * Метрики плееров зрителей (CMCD, Cloudflare): отдельной карточкой с
 * подзаголовком, что данные от зрителей, а не от энкодера. Графики внутри
 * контурные, чтобы не было карточки в карточке одного цвета.
 */
export const PlayerMetricsSection: FC<PlayerMetricsSectionProps> = ({
  player,
  unit,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();

  const points = player?.points ?? [];
  const byResolution = player?.byResolution ?? [];
  const byCountry = player?.byCountry ?? [];
  const hasData =
    points.length > 0 || byResolution.length > 0 || byCountry.length > 0;
  const stallsReported = hasStallData(points);

  return (
    <Card>
      <CardHeader
        title={t("streams.monitoring.player.cardTitle")}
        subheader={t("streams.monitoring.player.subtitle")}
        slotProps={{ title: { variant: "subtitle1" } }}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        {!hasData && !loading ? (
          <Alert severity="info">{t("streams.monitoring.player.noData")}</Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <MonitoringLineChart
                outlined
                title={t("streams.monitoring.player.viewersTitle")}
                hint={t("streams.monitoring.player.viewersInfo")}
                unit={unit}
                series={[
                  {
                    label: "",
                    data: points.map((point) => ({
                      x: point.at,
                      y: point.viewers,
                    })),
                  },
                ]}
                loading={loading}
                empty={points.length === 0}
                emptyLabel={t("streams.monitoring.noData")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <MonitoringLineChart
                outlined
                title={t("streams.monitoring.player.bufferTitle")}
                hint={t("streams.monitoring.player.bufferInfo")}
                yLabel={t("streams.monitoring.units.seconds")}
                unit={unit}
                series={[
                  {
                    label: "",
                    data: points.map((point) => ({
                      x: point.at,
                      y: point.bufferLengthSeconds ?? null,
                    })),
                  },
                ]}
                loading={loading}
                empty={points.length === 0}
                emptyLabel={t("streams.monitoring.noData")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <MonitoringLineChart
                outlined
                title={t("streams.monitoring.player.stallsTitle")}
                hint={t("streams.monitoring.player.stallsInfo")}
                yLabel={t("streams.monitoring.units.seconds")}
                unit={unit}
                series={[
                  {
                    label: "",
                    data: points.map((point) => ({
                      x: point.at,
                      y: point.bufferStarvationSeconds ?? null,
                    })),
                  },
                ]}
                loading={loading}
                empty={!stallsReported}
                emptyLabel={
                  points.length === 0
                    ? t("streams.monitoring.noData")
                    : t("streams.monitoring.player.stallsNoData")
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BucketListCard
                title={t("streams.monitoring.player.resolutions")}
                items={byResolution}
                loading={loading}
                renderKey={formatResolution}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BucketListCard
                title={t("streams.monitoring.player.countries")}
                items={byCountry}
                loading={loading}
                renderKey={(key) => formatCountry(key, i18n.language)}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
